"use client";

import { type ReactNode, useEffect, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
  useSubscriptionGate,
  type MinTierRequirement,
} from "@/hooks/useSubscriptionGate";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionGateContext } from "@/lib/billing/subscriptionContext";
import {
  TrialConversionModal,
  type ConversionVariant,
} from "@/components/billing/TrialConversionModal";
import { SubscriptionExpiredUpsell } from "@/components/billing/SubscriptionExpiredUpsell";

// ─── Component ────────────────────────────────────────────────────────────────

type Props = {
  children: ReactNode;
  /**
   * Minimum subscription tier required to pass through this gate.
   *
   * - "any_paid" — any non-free active subscription (default)
   * - "plus"     — Founder Plus, Pro, or Enterprise
   * - "pro"      — Founder Pro or Enterprise only
   */
  minTier?: MinTierRequirement;
};

/**
 * SubscriptionGate — Polar.sh subscription gating middleware for React Router SPAs
 *
 * This is the primary route-level guard for all Polar.sh subscription checks.
 * It replaces per-route ad-hoc checks with a single, consistent, testable
 * component that evaluates access and injects subscription state into context.
 *
 * ## What it checks (in order):
 * 1. Clerk authentication — unauthenticated users are sent to /pricing
 * 2. Active Polar.sh subscription — reads from both the authoritative
 *    `subscriptions` table (webhook-populated) and the `getMyEntitlement`
 *    query (which includes the server-configured grace period)
 * 3. 24-hour client-side grace buffer — admits users within 24 hours of
 *    expiry even if the server hasn't received/processed the Polar webhook yet
 * 4. Minimum tier requirement — enforces "any_paid" | "plus" | "pro" tiers
 *
 * ## On failure:
 * - auth_required / user_not_found: redirect to /pricing (user not signed in)
 * - no_active_subscription (expired/cancelled): show SubscriptionExpiredUpsell
 *   inline — user stays on the route and sees contextual resubscribe CTAs
 * - tier_insufficient (trialing): show FeatureLockedScreen + TrialConversionModal
 * - tier_insufficient (paid): redirect to /pricing?upgrade=1&plan=...
 *
 * ## On success:
 * - Renders children inside `SubscriptionGateContext.Provider`
 * - Children read tier/featureFlags via `useSubscriptionContext()` without
 *   making additional Convex queries
 * - Note: SubscriptionBanner and TrialConversionModal for proactive prompts
 *   are rendered by DashboardLayout, not here
 *
 * ## Usage in App.tsx:
 * ```tsx
 * // Any paid tier (default)
 * <Route path="/dashboard" element={
 *   <SubscriptionGate><DashboardPage /></SubscriptionGate>
 * } />
 *
 * // Plus or above
 * <Route path="/dashboard/advanced-stats" element={
 *   <SubscriptionGate minTier="plus"><AdvancedStatsPage /></SubscriptionGate>
 * } />
 *
 * // Pro or above
 * <Route path="/dashboard/priority-queue" element={
 *   <SubscriptionGate minTier="pro"><PriorityQueuePage /></SubscriptionGate>
 * } />
 * ```
 *
 * ## Reading subscription state in child components:
 * ```tsx
 * import { useSubscriptionContext } from "@/lib/billing/subscriptionContext";
 *
 * function CreateServerButton() {
 *   const { featureFlags, tier } = useSubscriptionContext();
 *   if (!featureFlags.canCreateOrg) return null;
 *   return <button>Create Server (tier: {tier})</button>;
 * }
 * ```
 */
export function SubscriptionGate({ children, minTier = "any_paid" }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const nextPath = buildNextPath(pathname, searchParams);

  const gate = useSubscriptionGate(nextPath, minTier);
  const subscription = useSubscription();
  const { isTrialing } = subscription;

  // ── Denial branch classification ───────────────────────────────────────────
  // Computed once gate data is loaded.

  // Case A: Trialing user hitting a higher-tier gate — show upgrade modal
  const showUpgradeModal =
    !gate.isLoading &&
    !gate.permitted &&
    gate.reason === "tier_insufficient" &&
    isTrialing;

  // Case B: Authenticated user with expired/cancelled subscription — show
  // inline upsell instead of redirecting (more contextual + actionable).
  const showExpiredUpsell =
    !gate.isLoading &&
    !gate.permitted &&
    gate.reason === "no_active_subscription";

  // Case C: Everything else (auth_required, user_not_found, tier_insufficient
  // for paid non-trialing users) — redirect to /pricing.
  const shouldRedirect =
    !gate.isLoading &&
    !gate.permitted &&
    !showUpgradeModal &&
    !showExpiredUpsell;

  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => {
    if (gate.isLoading) return;
    if (!gate.permitted) {
      if (showUpgradeModal) {
        setUpgradeModalOpen(true);
      } else if (shouldRedirect) {
        router.replace(gate.redirectPath);
      }
      // showExpiredUpsell: no redirect — component renders the upsell inline
    }
  }, [
    gate.isLoading,
    gate.permitted,
    gate.redirectPath,
    router,
    showUpgradeModal,
    shouldRedirect,
  ]);

  // Case B: Expired/cancelled subscription — show inline upsell
  if (showExpiredUpsell) {
    return (
      <SubscriptionExpiredUpsell
        status={subscription.status}
        expiredAt={subscription.currentPeriodEnd ?? undefined}
        returnPath={nextPath}
      />
    );
  }

  // Case A: Trialing user hitting a tier gate — show locked screen + upgrade modal
  if (showUpgradeModal) {
    const requiredTier: "plus" | "pro" =
      minTier === "pro" ? "pro" : "plus";
    return (
      <>
        <FeatureLockedScreen requiredTier={requiredTier} />
        <TrialConversionModal
          open={upgradeModalOpen}
          onClose={() => {
            setUpgradeModalOpen(false);
            router.push("/dashboard");
          }}
          variant={"tier_upgrade" as ConversionVariant}
          requiredTier={requiredTier}
        />
      </>
    );
  }

  // Loading or Case C redirect pending — render spinner
  if (gate.isLoading || !gate.permitted) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Access granted — provide subscription context to the entire subtree
  const isTrialingGate = gate.status === "trialing";
  const trialEndsAt = isTrialingGate ? (gate.currentPeriodEnd ?? null) : null;

  return (
    <SubscriptionGateContext.Provider
      value={{
        tier: gate.tier,
        status: gate.status,
        hasAccess: gate.permitted,
        isInGracePeriod: gate.isInGracePeriod,
        graceEndsAt: gate.clientGraceEndsAt,
        featureFlags: gate.featureFlags,
        isAdmin: gate.isAdmin,
        polarSubscriptionId: gate.polarSubscriptionId,
        currentPeriodEnd: gate.currentPeriodEnd,
        isTrialing: isTrialingGate,
        trialEndsAt,
      }}
    >
      {children}
    </SubscriptionGateContext.Provider>
  );
}

// ─── FeatureLockedScreen ──────────────────────────────────────────────────────

/**
 * Placeholder shown behind the upgrade modal when a trialing user reaches a
 * higher-tier gated route. Prevents premium content from rendering while
 * the TrialConversionModal is open.
 */
function FeatureLockedScreen({ requiredTier }: { requiredTier: "plus" | "pro" }) {
  const tierName = requiredTier === "pro" ? "Founder Pro" : "Founder Plus";
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="rounded-full bg-foreground/5 p-4">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="h-8 w-8 text-muted"
          aria-hidden="true"
        >
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path
            d="M8 11V7a4 4 0 118 0v4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>
        <h2 className="text-xl font-semibold text-foreground">
          Feature Locked
        </h2>
        <p className="mt-1 text-sm text-muted">
          This feature requires {tierName} or above.
        </p>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildNextPath(pathname: string, searchParams: URLSearchParams): string {
  const search = searchParams.toString();
  return `${pathname}${search ? `?${search}` : ""}`;
}
